import React, { useState, useEffect, useCallback, useMemo } from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import { USER } from "common/viewContent";
import * as Path from "routes/Path/index";
import {
  AddRewardLeaderboard,
  exportFileGameLeaderBoard,
  gameLeaderboardListApi,
  updateLeaderboardApi,
} from "store/actions";
import { toast } from "react-toastify";
import Table from "components/Table";
import Columns from "./Columns";
import UpdateLeaderboardModal from "components/Modals/UpdateLeaderboard";
import FormikForm from "components/shared/FormikForm";
import moment from "moment-timezone";
import usePermission from "hooks/usePermission";
import {
  generateWeekDayOptions,
  generateWeekOptions,
  generateYearOptions,
} from "constants";
import AddLeaderboardTransactions from "components/Modals/AddLeaderboardTransactions/index";
import { Dropdown } from "react-bootstrap";
import PermissionGate from "components/PermissionGate";

const InputFieldsForSearch = [
  {
    name: "search",
    type: "text",
    label: "",
    placeholder: "Search...",
  },
  {
    name: "weekDayNo",
    type: "select",
    label: "",
    placeholder: "Select Week Day...",
    options: generateWeekDayOptions(),
  },
  {
    name: "weekNo",
    type: "select",
    label: "",
    placeholder: "Select Week...",
    options: generateWeekOptions("Etc/GMT-4", { weekStartsOn: 1 }),
  },
  {
    name: "year",
    type: "select",
    label: "",
    placeholder: "Select Year...",
    options: generateYearOptions(),
  },
];
const initialValuesForFilter = {
  search: "",
  weekDayNo: moment().tz("Etc/GMT-4").isoWeekday(),
  weekNo: moment().tz("Etc/GMT-4").isoWeek(),
  year: moment().tz("Etc/GMT-4").isoWeekYear(),
};

const List = () => {
  const dispatch = useDispatch();
  const { gameLeaderboardList, loading, totalLeaderboard } = useSelector(
    ({ Game }) => Game,
  );
  const [columns, setcolumns] = useState([{ dataField: "", text: "" }]);
  const [showUpdatePop, setShowUpdatePop] = useState({
    show: false,
    rowData: null,
  });
  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    order: -1,
    orderBy: "date_created_utc",
    search: "",
    status: "",
    weekDayNo: moment().isoWeekday(),
    weekNo: moment().isoWeek(),
    year: moment().isoWeekYear(),
  });
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { hasPermission } = usePermission();
  const permissions = useMemo(() => ({
    add: false,
    edit: hasPermission("edit_leaderboard_setting"),
    delete: false,
  }), [hasPermission]);

  useEffect(() => {
    getApiData(filter);
  }, [filter]);

  useEffect(() => {
    setcolumns(
      Columns({
        handleEditHandler,
        handleOpenRewardModal,
        permissions,
      })?.filter((item) => {
        if (item?.text === "Action" && !permissions.edit) {
          return false;
        }
        return true;
      }),
    );
  }, [permissions.edit]);

  async function getApiData(query) {
    try {
      dispatch(gameLeaderboardListApi({ data: query }));
    } catch (error) { }
  }

  const handleOpenRewardModal = (row) => {
    setSelectedRow(row); // <-- REQUIRED
    setShowRewardModal(true);
  };

  const handleEditHandler = (info) => {
    setShowUpdatePop({
      show: true,
      rowData: info,
    });
  };

  const handleConfirmUpdate = (values) => {
    if (showUpdatePop.rowData) {
      const payload = {
        _id: showUpdatePop?.rowData?._id,
        points: values?.points || 0,
      };
      dispatch(
        updateLeaderboardApi({
          data: payload,
          callBack: (err, response) => {
            if (err) return;
            getApiData(filter);
            setShowUpdatePop({ show: false, rowData: null });
          },
        }),
      );
    }
  };

  // const handleFilterForm = (info) => {
  //   setFilter({
  //     ...filter,
  //     page: 1,
  //     limit: 20,
  //     order: -1,
  //     orderBy: "date_created_utc",
  //     search: info.search,
  //     status: info.status,
  //     weekNo: info.weekNo,
  //     year: info.year,
  //   });
  // };

  const handleFilterForm = (info) => {
    const newFilter = {
      ...filter,
      page: 1,
      limit: 20,
      order: -1,
      orderBy: "date_created_utc",
      search: info.search,
      status: info.status,
      weekNo: info.weekNo,
      year: info.year,
    };

    // If user selected a specific day → include weekDayNo
    if (info.weekDayNo !== "") {
      newFilter.weekDayNo = info.weekDayNo;
    } else {
      // If user selected "All Days" → remove weekDayNo filter
      delete newFilter.weekDayNo;
    }

    setFilter(newFilter);
  };

  const handleAddTask = async (values) => {
    try {
      // Add safety check
      if (!selectedRow?._id) {
        console.error("❌ No selected row or userId");
        toast.error("No user selected");
        return;
      }

      // values already contains: coinName, coinSymbol, coinIcon from modal
      const payload = {
        userId: selectedRow.userId,
        leaderBoardWinnerId: selectedRow._id,
        coinName: values.coinName,
        coinSymbol: values.coinSymbol,
        coinIcon: values.coinIcon,
        // amount: Number(values.numberOfAmount),
      };

      const callback = (err, res) => {
        if (err) {
          toast.error(err || "Failed to add reward");
          return;
        }

        toast.success("Reward added successfully");
        setShowRewardModal(false);
        setSelectedRow(null);
        getApiData(filter);
      };

      dispatch(
        AddRewardLeaderboard({
          data: payload,
          callback: callback,
        }),
      );
    } catch (error) {
      console.error("💥 Error in handleAddTask:", error);
      toast.error("Failed to add reward");
    }
  };

  const handleExport = (type) => {
    const body = {
      page: 1,
      limit: 20,
      order: -1,
      orderBy: "date_created_utc",
      search: filter.search,
      weekNo: filter.weekDayNo,
      year: filter.year,
      downloadType: type,
    };

    const callback = (err, res) => {
      if (err) {
        toast.error(err?.message || "something went wrong");
      } else {
        toast.success(res?.message || "file exported successfully!");
      }
    };
    dispatch(exportFileGameLeaderBoard({ data: body, callback }));
  };

  return (
    <>
      <Helmet title={"Game leaderboard"} />

      <AddLeaderboardTransactions
        show={showRewardModal}
        handleConfirm={handleAddTask}
        handleClose={() => setShowRewardModal(false)}
        rowData={selectedRow}
      />

      {showUpdatePop.show && (
        <UpdateLeaderboardModal
          show={showUpdatePop.show}
          title={"Update Leaderboard Points"}
          loading={loading}
          handleClose={() => setShowUpdatePop({ show: false, rowData: null })}
          handleConfirm={handleConfirmUpdate}
          initialValues={showUpdatePop?.rowData ? showUpdatePop?.rowData : null}
        />
      )}
      <section className="section">
        <div className="section-header">
          <h1>Game leaderboard</h1>

          {/* <div className="section-header-breadcrumb">
            <div className="breadcrumb-item active">
              <Link to={"/admin/" + Path.dashboard}>Dashboard</Link>
            </div>
            <div className="breadcrumb-item">Game leaderboard</div>
          </div> */}
        </div>
        <div className="section-body">
          <div className="card">
            <div className="row justify-content-between">
              <div className="col-6">
                <div className="card-header">
                  <h2 className="section-title">Listing</h2>
                </div>
              </div>
              <PermissionGate permission="exports_leaderboard">
                <div className="col-6">
                  <div className="card-header d-flex justify-content-end align-items-center">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="dropdown-basic">
                        Export
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleExport("csv")}>
                          CSV
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleExport("xlsx")}>
                          Xlsx
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </PermissionGate>

              <div className="col-12">
                <div className="card-header">
                  <FormikForm
                    initialValues={initialValuesForFilter}
                    onSubmit={handleFilterForm}
                    loading={loading}
                    inputFields={InputFieldsForSearch}
                    buttonText={"Search"}
                  />
                </div>
              </div>
            </div>
          </div>
          {gameLeaderboardList?.length > 0 && (
            <Table
              data={gameLeaderboardList}
              columns={columns}
              setFilter={setFilter}
              total={totalLeaderboard}
              filter={filter}
            />
          )}
          {!loading && gameLeaderboardList?.length === 0 && (
            <p style={{ textAlign: "center" }}>No Data Found</p>
          )}
        </div>
      </section>
    </>
  );
};

export default List;
